import React, { useEffect, useMemo, useState } from 'react';
import { FaTimes, FaCheck, FaArrowLeft, FaArrowRight, FaInfoCircle, FaFileAlt, FaEdit, FaEye, FaSave, FaTrash } from 'react-icons/fa';
import Button from '../components/buttons/Button';
import Card from '../components/Card/Card';
import Notification from '../components/Notifications/Notification';
import useNotification from '../components/Notifications/useNotifications';
import { reportTypeService, customInstructionService } from '../services';
import { createLogger } from '../services/Debug';
import { isCapacitor } from '../utils/platform';
import '../Templates/TemplateBuilder/TemplateBuilderWizard.css';
import Input from '../components/inputs/Input';
import FullScreenTextEditor from '../components/editors/FullScreenTextEditor';

const STEPS = [
  { id: 1, title: 'Nom', icon: FaFileAlt },
  { id: 2, title: 'Type', icon: FaEdit },
  { id: 3, title: 'Exemples', icon: FaEye },
  { id: 4, title: 'Instructions', icon: FaSave }
];

const TYPE_OPTIONS = [
  { value: 'rapport_long', label: 'Rapport long' },
  { value: 'examen_technique', label: 'Examen technique' },
  { value: 'rapport_court', label: 'Rapport court' },
  { value: 'autre', label: 'Autre' }
];

function ReportTypeCreatorOverlay({ isOpen, onClose, onCreated, existingNames = [] }) {
  const log = createLogger('ReportTypeCreatorOverlay');
  const { notification, showNotification, hideNotification } = useNotification();

  const [currentStep, setCurrentStep] = useState(1);
  const [isSaving, setIsSaving] = useState(false);

  // Form state
  const [name, setName] = useState('');
  const [category, setCategory] = useState('rapport_long');
  const [examples, setExamples] = useState(['']);
  const [instructions, setInstructions] = useState('');
  const [activeExampleEditor, setActiveExampleEditor] = useState({ open: false, index: -1, initial: '' });
  const [instructionEditorOpen, setInstructionEditorOpen] = useState(false);

  const [nameError, setNameError] = useState('');
  const [nameExists, setNameExists] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    // reset on open
    setCurrentStep(1);
    setIsSaving(false);
    setName('');
    setCategory('rapport_long');
    setExamples(['']);
    setInstructions('');
    setNameError('');
    setActiveExampleEditor({ open: false, index: -1, initial: '' });
    setInstructionEditorOpen(false);
  }, [isOpen]);

  // No auto-open for full-screen editor; open on user action

  function validateName(value) {
    if (!value) return false;
    if (value.length < 3) return false;
    if (value.length > 50) return false;
    // Allow letters, spaces, basic latin accents
    return /^[A-Za-zÀ-ÖØ-öø-ÿ\s]+$/.test(value);
  }

  const canProceed = useMemo(() => {
    if (currentStep === 1) {
      return validateName(name) && !nameExists;
    }
    if (currentStep === 2) return !!category; // type selection
    if (currentStep === 3) return true; // examples optional
    if (currentStep === 4) return true; // instructions optional
    return false;
  }, [currentStep, name, category]);

  const handleNext = () => {
    if (!canProceed) return;
    setCurrentStep((s) => Math.min(s + 1, STEPS.length));
  };

  const handlePrev = () => setCurrentStep((s) => Math.max(s - 1, 1));

  const handleCreate = async () => {
    if (!validateName(name) || nameExists) {
      setCurrentStep(1);
      setNameError(nameExists ? 'Ce nom existe déjà' : 'Nom invalide');
      showNotification('Nom invalide', 'error');
      return;
    }

    setIsSaving(true);
    try {
      const payload = {
        name: name.trim(),
        category: category,
        customInstruction: (instructions || '').trim() || undefined
      };
      onClose?.();
      const validExamples = (examples || []).filter((ex) => (ex || '').trim() !== '');
      if (validExamples.length > 0) {
        await reportTypeService.createReportTypeWithExamples({ ...payload, examples: validExamples });
        showNotification(`Type créé avec ${validExamples.length} exemple(s). Le traitement peut prendre quelques minutes.`, 'success');
      } else {
        await reportTypeService.createReportType(payload);
        showNotification('Type créé avec succès.', 'success');
      }

      if (onCreated) onCreated({ name: name.trim() });
    } catch (e) {
      log.error('Failed to create report type', e);
      showNotification("Erreur lors de la création du modèle", 'error');
    } finally {
      setIsSaving(false);
    }
  };

  // Example helpers (same logic as old create panel)
  const addExample = () => {
    setExamples((prev) => (prev.length < 10 ? [...prev, ''] : prev));
  };
  const removeExample = (index) => {
    setExamples((prev) => (prev.length > 1 ? prev.filter((_, i) => i !== index) : prev));
  };
  const updateExample = (index, value) => {
    setExamples((prev) => {
      const copy = [...prev];
      copy[index] = value;
      return copy;
    });
  };

  if (!isOpen) return null;

  return (
    <div className="tbw-overlay" onClick={onClose}>
      <div className={isCapacitor ? "tbw-modal-capacitor" : "tbw-modal"}  onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="tbw-header">
          <h2>Créer un modèle</h2>
          <button className="tbw-close" onClick={onClose} aria-label="Fermer">
            <FaTimes size={20} />
          </button>
        </div>

        {notification && (
          <Notification
            id={notification.id}
            message={notification.message}
            type={notification.type}
            onClose={hideNotification}
          />
        )}

        {/* Step Indicator */}
        <div className="tbw-steps">
          {STEPS.map((step) => {
            const Icon = step.icon;
            const accessible = step.id < currentStep || step.id === currentStep;
            return (
              <div
                key={step.id}
                className={`tbw-step ${currentStep === step.id ? 'active' : ''} ${currentStep > step.id ? 'completed' : ''} ${!accessible ? 'disabled' : ''}`}
                onClick={() => accessible && step.id < currentStep && setCurrentStep(step.id)}
              >
                <div className="tbw-step-number">
                  {currentStep > step.id ? <FaCheck size={12} /> : <Icon size={16} />}
                </div>
                <div className="tbw-step-title">{step.title}</div>
              </div>
            );
          })}
          <div
            className="tbw-step-progress"
            style={{ width: `${((currentStep - 1) / (STEPS.length - 1)) * 100}%` }}
          />
        </div>

        {/* Content */}
        <div className="tbw-content" >
          {currentStep === 1 && (
            <Card >
              <h3>Nom du modèle</h3>
              <div className="rtc-field">
                <Input
                  id="rt-name"
                  type="text"
                  label="Nom"
                  value={name}
                  onChange={(e) => {
                    const v = e.target.value;
                    setName(v);
                    setNameError('');
                    const exists = (existingNames || []).some((n) => String(n).toLowerCase() === String(v).trim().toLowerCase());
                    setNameExists(exists);
                  }}
                  placeholder="Nom du modèle (ex: Consultation cardiologie)"
                  required
                />
                {((name && name.length < 3) || name.length > 50 || !/^[A-Za-zÀ-ÖØ-öø-ÿ\s]+$/.test(name || '') || nameExists || nameError) && (
                  <div className="rtc-error" style={{ color: '#c0392b', fontSize: 12, fontStyle: 'italic', marginTop: 6 }}>
                    {[
                      name && name.length < 3 ? 'Au moins 3 caractères' : null,
                      name && name.length > 50 ? 'Maximum 50 caractères' : null,
                      name && !/^[A-Za-zÀ-ÖØ-öø-ÿ\s]+$/.test(name || '') ? 'Lettres uniquement' : null,
                      nameExists ? 'Ce nom existe déjà' : null,
                      nameError || null,
                    ].filter(Boolean).join(' • ')}
                  </div>
                )}
                <div className="rtc-hint" style={{ marginTop: 6, color: '#666', fontSize: 12, fontStyle: 'italic' }}>
                  Le nom du modèle qui s'affichera et que vous pourrez dicter (ex. « Je dicte un rapport d'assurance »).
                </div>
              </div>
            </Card>
          )}

          {currentStep === 2 && (
            <Card className="rtc-card">
  <h3>Catégorie du modèle</h3>
              <div className="rtc-field">
                <div className="rtc-type-grid" style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {TYPE_OPTIONS.map((opt) => (
                    <Button
                      key={opt.value}
                      variant={category === opt.value ? 'primary' : 'secondary'}
                      isSelected={category === opt.value}
                      type="fixed"
                      onClick={() => setCategory(opt.value)}
                    >
                      {opt.label}
                    </Button>
                  ))}
                </div>
                <div className="rtc-hint" style={{ marginTop: 6, color: '#666', fontSize: 12, fontStyle: 'italic', display: 'flex', alignItems: 'center', gap: 6 }}>
                  <FaInfoCircle /> Choisissez la catégorie du modèle qui correspond le mieux à ce que vous souhaitez créer.
                </div>
              </div>
            </Card>
          )}

          {currentStep === 3 && (
            <Card className="rtc-card">
              <h3>Ajouter des exemples (facultatif)</h3>
              <div className="rtc-field" style={{ display: 'grid', gap: 8 }}>
                <div className="rtc-hint" style={{ color: '#666', fontSize: 12, fontStyle: 'italic' }}>
                  Ajoutez un ou plusieurs exemples représentatifs (optionnel). De bons exemples améliorent la qualité.
                </div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Button 
                    onClick={addExample}
                    disabled={examples.length >= 10}
                    variant="secondary"
                    type="fixed"
                  >
                    + Ajouter un exemple
                  </Button>
                </div>
                <div style={{ display: 'grid', gap: 8 }}>
                  {examples.map((ex, index) => (
                    <div key={index} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      <div style={{ flex: 1, minHeight: 38, border: '1px solid #e5e7eb', borderRadius: 8, padding: '8px 10px', background: '#fafafa', color: '#555' }}>
                        {(ex || '').trim() ? `${(ex || '').trim().slice(0, 80)}${(ex || '').trim().length > 80 ? '…' : ''}` : `Exemple ${index + 1}`}
                      </div>
                      <Button
                        variant="secondary"
                        icon={FaEdit}
                        type="fixed"
                        onClick={() => setActiveExampleEditor({ open: true, index, initial: ex })}
                      >
                        Éditer
                      </Button>
                      <Button
                        icon={FaTrash}
                        type="fixed"
                        onClick={() => removeExample(index)}
                        title="Supprimer cet exemple"
                        variant="danger"
                        fontSize="small"
                        disabled={examples.length <= 1}
                      >
                        −
                      </Button>
                    </div>
                  ))}
                </div>
                <div className="rtc-hint" style={{ marginTop: 6 }}>
                  {examples.filter(ex => (ex || '').trim() !== '').length} / 10 exemples
                </div>
              </div>
            </Card>
          )}

          {currentStep === 4 && (
            <Card className="rtc-card">
              <h3>Instructions personnalisées (facultatif)</h3>
              <div className="rtc-field" style={{ display: 'grid', gap: 8 }}>
                <div
                  role="button"
                  onClick={() => setInstructionEditorOpen(true)}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setInstructionEditorOpen(true); }}
                  tabIndex={0}
                  style={{ minHeight: 80, padding: 10, border: '1px solid #e0e0e0', borderRadius: 8, background: '#fafafa', color: '#333', whiteSpace: 'pre-wrap', overflow: 'hidden', cursor: 'text' }}
                  title={instructions || ''}
                >
                  {instructions || 'Saisir des instructions personnalisées...'}
                </div>
                <div className="rtc-hint" style={{ color: '#666', fontSize: 12, fontStyle: 'italic' }}>
                  Ces consignes guident la génération (ex. « Toujours commencer par l'indication », « Style direct »).
                </div>
              </div>
            </Card>
          )}
        </div>

        {/* Footer */}
        <div className="tbw-footer">
          <div className="tbw-footer-left">
            <Button variant="secondary" icon={FaArrowLeft} onClick={handlePrev} disabled={currentStep === 1 || isSaving}>
              Précédent
            </Button>
          </div>
          <div className="tbw-footer-center">Étape {currentStep} sur {STEPS.length}</div>
          {currentStep < 4 && (
            <Button variant="primary" icon={FaArrowRight} onClick={handleNext} disabled={!canProceed || isSaving}>
              Suivant
            </Button>
          )}
          {currentStep === 4 && (
            <Button variant="success" icon={FaCheck} onClick={handleCreate} disabled={isSaving}>
              {isSaving ? 'Création…' : 'Créer'}
            </Button>
          )}
        </div>
      </div>
      {/* Fullscreen editors */}
      {activeExampleEditor?.open && (
        <div onClick={(e) => e.stopPropagation()}>
          <FullScreenTextEditor
            isOpen={true}
            onClose={() => setActiveExampleEditor({ open: false, index: -1, initial: '' })}
            onSave={async (val) => { updateExample(activeExampleEditor.index, val); }}
            initialValue={activeExampleEditor.initial || ''}
            title={`Exemple ${Number(activeExampleEditor.index) + 1}`}
            placeholder={'Collez votre exemple ici...'}
            showCharCount={true}
            saveButtonText={'Enregistrer'}
          />
        </div>
      )}
      {instructionEditorOpen && (
        <div onClick={(e) => e.stopPropagation()}>
          <FullScreenTextEditor
            isOpen={true}
            onClose={() => { setInstructionEditorOpen(false); }}
            onSave={async (val) => { setInstructions((val || '').trim()); setInstructionEditorOpen(false); }}
            initialValue={instructions}
            title={'Instructions personnalisées'}
            placeholder={"Ex: Toujours commencer par l'indication. Utiliser un style direct."}
            showCharCount={true}
            saveButtonText={'Enregistrer les instructions'}
          />
        </div>
      )}
    </div>
  );
}

export default ReportTypeCreatorOverlay;
